import { Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import {
  Select,
  InputText,
  DateInput,
  ButtonSecondary,
  ButtonSize,
  IconExpand,
  IconTrash,
} from '@ui/src';
import RecordEntityHeader from './record-entity-header';
import { AddRecordFormTypes } from './types';
import {
  EntityAddressesFormValues,
  EntityAliasesFormValues,
  EntityIdentificationsFormValues,
  EntityPositionsFormValues,
} from '../../hoc/types';
import AddressEntity from './address-entity';
import AliasEntity from './alias-entity';
import IdentificationEntity from './identifications-entity';
import PositionEntity from './entity-position';

import cn from './blacklist.module.css';

const AddRecord = ({
  control,
  errors,
  setValue,
  dobs,
  addresses,
  setAddresses,
  aliases,
  setAliases,
  identifications,
  setIdentifications,
  otherRecordErrors,
  positions,
  setPositions,
}: AddRecordFormTypes) => {
  const [dob, setDob] = useState<{ value: string; id: string }[]>([]);

  useEffect(() => {
    if (dobs) {
      setDob(
        dobs?.map((item, index) => ({
          value: item,
          id: (index + 1).toString(),
        })),
      );
    }
  }, [dobs]);

  return (
    <div className={cn.addRecordFormWrapper}>
      <div className={cn.addRecordForm}>
        <div className={`${cn.addRecordFormBlock} ${cn.withBorder}`}>
          <div className={cn.addRecordFormBlockTitle}>General Info</div>
          <div className={cn.addRecordFormBlockContent}>
            <div className={cn.addRecordFormBlockValue}>
              <Controller
                control={control}
                defaultValue=""
                name="entityTypeDesc"
                render={({ field: { value, name, onChange } }) => (
                  <Select
                    title="Type"
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder="Select type"
                    list={[
                      { name: 'Individual', value: 'INDIVIDUAL' },
                      { name: 'Company', value: 'ORGANIZATION' },
                      { name: 'Vessel', value: 'VESSEL' },
                      { name: 'Aircraft', value: 'AIRCRAFT' },
                      { name: 'Country', value: 'COUNTRY' },
                    ]}
                    error={errors?.entityTypeDesc?.message}
                    withClearButton
                    fullWidth
                  />
                )}
              />
            </div>
            <div className={cn.addRecordFormBlockValue}>
              <Controller
                control={control}
                defaultValue=""
                name="gender"
                render={({ field: { value, name, onChange } }) => (
                  <Select
                    title="Gender"
                    name={name}
                    value={value}
                    placeholder="Select gender"
                    onChange={onChange}
                    list={[
                      { name: 'Male', value: 'MALE' },
                      { name: 'Female', value: 'FEMALE' },
                    ]}
                    error={errors?.gender?.message}
                    withClearButton
                    fullWidth
                  />
                )}
              />
            </div>
          </div>
          <div className={cn.addRecordFormBlockContent}>
            <div className={`${cn.addRecordFormBlockValue} ${cn.lessWidth}`}>
              <Controller
                control={control}
                defaultValue=""
                name="firstName"
                render={({ field: { value, name, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="First Name"
                    placeholder="Enter First Name"
                    error={errors?.firstName?.message}
                  />
                )}
              />
            </div>
            <div className={`${cn.addRecordFormBlockValue} ${cn.lessWidth}`}>
              <Controller
                control={control}
                defaultValue=""
                name="lastName"
                render={({ field: { value, name, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="Last Name"
                    placeholder="Enter Last Name"
                    error={errors?.lastName?.message}
                  />
                )}
              />
            </div>
            <div className={`${cn.addRecordFormBlockValue} ${cn.lessWidth}`}>
              <Controller
                control={control}
                defaultValue=""
                name="middleName"
                render={({ field: { value, name, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="Middle Name"
                    placeholder="Enter Middle Name"
                    error={errors?.firstName?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className={cn.addRecordFormBlockContent}>
            <div className={`${cn.addRecordFormBlockValue} ${cn.lessWidth}`}>
              <Controller
                control={control}
                defaultValue=""
                name="prefix"
                render={({ field: { value, name, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="Prefix"
                    placeholder="Enter Prefix"
                    error={errors?.prefix?.message}
                  />
                )}
              />
            </div>
            <div className={`${cn.addRecordFormBlockValue} ${cn.lessWidth}`}>
              <Controller
                control={control}
                defaultValue=""
                name="suffix"
                render={({ field: { value, name, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="Suffix"
                    placeholder="Enter Suffix"
                    error={errors?.suffix?.message}
                  />
                )}
              />
            </div>
            <div className={`${cn.addRecordFormBlockValue} ${cn.lessWidth}`}>
              <Controller
                control={control}
                defaultValue=""
                name="title"
                render={({ field: { value, name, onChange } }) => (
                  <InputText
                    name={name}
                    value={value}
                    onChange={onChange}
                    title="Title"
                    placeholder="Enter Title"
                    error={errors?.title?.message}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className={cn.addRecordFormBlock}>
          <div className={cn.addRecordFormBlockTitle}>Date of birth</div>
          {dob?.map((item, index) => (
            <div className={cn.dobWrapper} key={`dob-${item.id}`}>
              <DateInput
                value={item.value}
                onChange={(date) => {
                  const nextDobs = dob?.map((dobItem) => {
                    const result = { ...dobItem };

                    if ((index + 1).toString() === dobItem.id) {
                      result.value = date?.toISOString();
                    }

                    return result;
                  });

                  const nextDobsValues = dobs?.map((dobValue, dobIndex) => {
                    let result = dobValue;

                    if (index === dobIndex) {
                      result = date?.toISOString();
                    }

                    return result;
                  });

                  setDob(nextDobs);
                  setValue('entityDOBs', nextDobsValues);
                }}
                placeholder="Select date"
                title="Date of Birth"
                className={cn.dateOfBirthPicker}
                withoutCustomSelector
              />
              <button
                type="button"
                className={cn.removeDobButton}
                onClick={() => {
                  const nextDobs = dob
                    ?.filter((dobItem) => dobItem.id !== (index + 1).toString())
                    .map((dobItem, dobIndex) => ({
                      value: dobItem.value,
                      id: (dobIndex + 1).toString(),
                    }));
                  const nextDobsValues = dobs?.filter(
                    (dobValue, dobValueIndex) => dobValueIndex !== index,
                  );

                  setDob(nextDobs);
                  setValue('entityDOBs', nextDobsValues);
                }}
              >
                <IconTrash />
              </button>
            </div>
          ))}
          <ButtonSecondary
            size={ButtonSize.SMALL}
            className={`button-white ${cn.addDobButton}`}
            text="Add Dates of birth"
            onClick={() => {
              const nextDobsValues = ([] as string[]).concat(dobs as string[]);
              const nextDobs = ([] as { value: string; id: string }[]).concat(
                dob,
              );

              nextDobsValues.push('');
              nextDobs.push({
                value: '',
                id: ((nextDobsValues?.length || 0) + 1).toString(),
              });

              setValue('entityDOBs', nextDobsValues);
              setDob(nextDobs);
            }}
            icon={<IconExpand />}
          />
          <Controller
            control={control}
            defaultValue={[]}
            name="entityDOBs"
            render={({ field: { value, name } }) => (
              <input type="hidden" name={name} value={value} />
            )}
          />
        </div>
        <div className={cn.addRecordFormBlock}>
          <div className={cn.addRecordFormBlockTitle}>Addresses</div>
          {addresses && addresses?.length > 0 && (
            <>
              <RecordEntityHeader
                titles={[
                  'Type',
                  'Address 1',
                  'Address 2',
                  'City',
                  'Country',
                  'Postal Code',
                  'ISO',
                ]}
              />
              {addresses?.map((item) => (
                <AddressEntity
                  key={item.id}
                  address={item}
                  setAddresses={setAddresses}
                  addresses={addresses}
                  length={7}
                  id={item.id}
                />
              ))}
            </>
          )}
          <ButtonSecondary
            size={ButtonSize.SMALL}
            className={`button-white ${cn.addDobButton}`}
            text="Add address"
            onClick={() => {
              const nextAddresses = ([] as EntityAddressesFormValues[]).concat(
                addresses as EntityAddressesFormValues[],
              );

              nextAddresses.push({
                id: `address-${(nextAddresses?.length || 0) + 1}`,
                addressTypeDesc: '',
                address1: '',
                address2: '',
                city: '',
                postalCode: '',
                country: '',
                isoStandard: '',
              });

              setAddresses(nextAddresses);
            }}
            icon={<IconExpand />}
          />
        </div>
        <div className={cn.addRecordFormBlock}>
          <div className={cn.addRecordFormBlockTitle}>Aliases</div>
          {aliases && aliases?.length > 0 && (
            <>
              <RecordEntityHeader
                titles={['Type', 'Name', 'Script', 'First', 'Last', 'Middle']}
              />
              {aliases?.map((item) => (
                <AliasEntity
                  key={item.id}
                  alias={item}
                  setAliases={setAliases}
                  aliases={aliases}
                  length={6}
                  id={item.id}
                />
              ))}
            </>
          )}
          <ButtonSecondary
            size={ButtonSize.SMALL}
            className={`button-white ${cn.addDobButton}`}
            text="Add alias"
            onClick={() => {
              const nextAliases = ([] as EntityAliasesFormValues[]).concat(
                aliases as EntityAliasesFormValues[],
              );

              nextAliases.push({
                id: `address-${(nextAliases?.length || 0) + 1}`,
                aliasTypeDesc: '',
                englishDescription: '',
                name: '',
                firstName: '',
                lastName: '',
                middleName: '',
              });

              setAliases(nextAliases);
            }}
            icon={<IconExpand />}
          />
        </div>
        <div className={cn.addRecordFormBlock}>
          <div className={cn.addRecordFormBlockTitle}>Identifications</div>
          {identifications && identifications?.length > 0 && (
            <>
              <RecordEntityHeader
                titles={[
                  'Type',
                  'Issuer',
                  'Number',
                  'Country',
                  'ISO',
                  'Issue Date',
                  'Expiration',
                ]}
              />
              {identifications?.map((item) => (
                <IdentificationEntity
                  key={item.id}
                  identification={item}
                  setIdentifications={setIdentifications}
                  identifications={identifications}
                  length={7}
                  id={item.id}
                />
              ))}
            </>
          )}
          <ButtonSecondary
            size={ButtonSize.SMALL}
            className={`button-white ${cn.addDobButton}`}
            text="Add Identification"
            onClick={() => {
              const nextIdentifications = (
                [] as EntityIdentificationsFormValues[]
              ).concat(identifications as EntityIdentificationsFormValues[]);

              nextIdentifications.push({
                id: `identification-${(nextIdentifications?.length || 0) + 1}`,
                administrativeUnitName: '',
                isoStandard: '',
                identificationIssuer: '',
                identificationTypeDesc: '',
                identificationNumber: '',
                issueDateValue: '',
                expirationDateValue: '',
              });

              setIdentifications(nextIdentifications);
            }}
            icon={<IconExpand />}
          />
        </div>
        <div className={cn.addRecordFormBlock}>
          <div className={cn.addRecordFormBlockTitle}>Positions</div>
          {positions &&
            positions?.length > 0 &&
            positions?.map((item) => (
              <PositionEntity
                key={item.id}
                position={item}
                setPositions={setPositions}
                positions={positions}
                id={item.id}
              />
            ))}
          <ButtonSecondary
            size={ButtonSize.SMALL}
            className={`button-white ${cn.addDobButton}`}
            text="Add position"
            onClick={() => {
              const nextPositions = ([] as EntityPositionsFormValues[]).concat(
                positions as EntityPositionsFormValues[],
              );

              nextPositions.push({
                id: `positions-${(nextPositions?.length || 0) + 1}`,
                position: '',
              });

              setPositions(nextPositions);
            }}
            icon={<IconExpand />}
          />
        </div>
      </div>
      {otherRecordErrors && otherRecordErrors?.length > 0 && (
        <div className={cn.otherErrorsContent}>
          <ul>
            {otherRecordErrors?.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AddRecord;
